<template>
  <div class="ai-assistant-wrapper gk-ai-assistant" :class="{ loading: loading }">
    <div class="content-wrap">
      <div class="conversation">
        <overlay-scrollbars ref="ai-conversation" :options="{ scrollbars: { autoHide: 'leave' }, overflowBehavior: { y: 'scroll', x: 'scroll' } }">
          <div class="content">
            <div v-for="(message, index) in conversation" :key="index" class="message" :class="['message--' + message.author]">
              <div class="message--avatar">
                <div v-if="(message.author === 'ai')"><font-awesome-icon icon="fa-solid fa-message-bot" /></div>
                <GkAvatar v-if="(message.author === 'user')" :userId="currentUser.id" />
              </div>
              <div class="message--text" :class="{ pending: !message.message }">
                <span class="message-tail">
                  <svg v-if="(message.author === 'ai')" viewBox="0 0 8 13" height="13" width="8" preserveAspectRatio="xMidYMid meet" class="" version="1.1" x="0px" y="0px" enable-background="new 0 0 8 13" xml:space="preserve"><path opacity="0.13" fill="#0000000" d="M1.533,3.568L8,12.193V1H2.812 C1.042,1,0.474,2.156,1.533,3.568z"></path><path fill="currentColor" d="M1.533,2.568L8,11.193V0L2.812,0C1.042,0,0.474,1.156,1.533,2.568z"></path></svg>
                  <svg v-if="(message.author === 'user')" viewBox="0 0 8 13" height="13" width="8" preserveAspectRatio="xMidYMid meet" class="" version="1.1" x="0px" y="0px" enable-background="new 0 0 8 13" xml:space="preserve"><path opacity="0.13" d="M5.188,1H0v11.193l6.467-8.625 C7.526,2.156,6.958,1,5.188,1z"></path><path fill="currentColor" d="M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z"></path></svg>
                </span>
                <div v-if="message.message" class="message--text__content" v-html="message.message" />
                <div v-else>
                  <svg id="dots" width="32px" height="32px" viewBox="0 0 132 58" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                      <g id="dots" fill="#3333337d">
                        <circle id="dot1" cx="25" cy="30" r="13"></circle>
                        <circle id="dot2" cx="65" cy="30" r="13"></circle>
                        <circle id="dot3" cx="105" cy="30" r="13"></circle>
                      </g>
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </overlay-scrollbars>
      </div>
      <div class="prompt-container">
        <div class="prompt">
          <b-form-textarea
            v-model="promptMessage"
            :placeholder="$gettext('Send a message.')"
            @keydown="sendPromptOnEnter"
            rows="4"
            no-resize
            :maxlength="maxPromptLength"
          />
          <GekkoButton 
            @click="sendMessage"
            :text="$gettext('Send')" 
            class="send-message-btn"
            :disabled="!promptMessage"
            :icon="'fa-solid fa-paper-plane-top'"
          />
          <div class="prompt__text-count" :class="{ maxfill: promptMessage.length >= maxPromptLength }">{{ promptMessage.length }}/{{ maxPromptLength }}</div>
        </div>
        <div class="disclaimer">{{ this.disclaimer }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GekkoButton from "@/components/GekkoButton";
import GkLogo from "@/components/ui/GkLogo.vue";
import AiAssistantService from "@/store/services/ai.assistant.service.js";

export default {
  name: 'ai-assistant',
  components: { 
    GekkoButton,
    GkLogo
  },
  props: {
    initialize: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      loading: true,
      promptMessage: '',
      waitingForResponse: false,
      maxPromptLength: null,
      disclaimer: null,
      intro: null,
      botId: null,
      initialized: false,
      conversation: []
    }
  },
  computed: {
    ...mapGetters ({ 
      currentUser: 'main/getCurrentUser',
      isPremium: 'getIsPremium',
      getCompanyId: 'main/getCompanyId'
    })
  },
  watch: {
    initialize (val) {
      if (!this.initialized && val) {
        this.initialized = true
        setTimeout(() => {
          this.initializeConversation()
        }, 300)
      }
    }
  },
  created () {
    // conversation scroll bottom
    this.scrollConversation()
    AiAssistantService.getCurrentFunctionCallingBot().then((response) => {
      this.maxPromptLength = response.data.max_prompt_length
      this.disclaimer = response.data.disclaimer
      this.intro = response.data.intro
      this.botId = response.data.id
    });
  },
  methods: {
    initializeConversation () {
      if (!this.conversation.map(c => c.message).includes(this.intro)) {
        this.conversation.push({ author: 'ai', message: null });
        setTimeout(() => {
          this.fillResponseMessage(this.intro)
        }, 500);
      }
    },
    sendPromptOnEnter (e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (this.promptMessage && !!this.promptMessage.trim()) {
          this.sendMessage()
        } else {
          this.promptMessage = null
        }
      }
    },
    sendMessage () {
      if (this.waitingForResponse) return

      this.conversation.push({
        author: 'user',
        message: this.promptMessage
      })
      this.scrollConversation()

      // trim prompt length to maxPromptLength
      const requestQuestion = this.promptMessage.substring(0, this.maxPromptLength)

      this.promptMessage = '' // reset textarea
      this.waitingForResponse = true

      setTimeout(() => { // TODO: remove timeout?
        this.conversation.push({
          author: 'ai',
          message: null // to show dots
        })
        this.scrollConversation()

        let messageData = {
          message: requestQuestion,
          bot_id: this.botId,
          company_id: this.getCompanyId
        }
        
        AiAssistantService.getFunctionCallingResponse(messageData).then((response) => {
          this.waitingForResponse = false;
          
          if (response.data.reply) this.fillResponseMessage(response.data.reply)

          /*if (response.data['resource_type'] === 'invoice') {
            this.fillResponseMessage(this.$gettext('Navigating to invoice...'))
            this.$router.push({ name: 'InvoiceView', params: { invoiceId: response.data['resource_id'] } })
          }*/
          if (response.data['resource_type'] === 'invoice') {
            this.fillResponseMessage(this.$gettext('Navigating to create invoices with our online invoice editor. Our flexible scheduler allows you to plan your invoicing in any way you want.'))
            this.$router.push({ name: 'InvoiceAdd' })
          } else if (response.data['resource_type'] === 'cost') {
            this.fillResponseMessage(this.$gettext('Navigating to Add Cost to create expenses based on invoices or receipts'))
            this.$router.push({ name: 'AddCost' })
          } else if (response.data['resource_type'] === 'cash_flow') {
            this.fillResponseMessage(this.$gettext('Navigating to Cash Flow to explore the evolution of your cash flow'))
            this.$router.push({ name: 'CashFlow' })
          } else if (response.data['resource_type'] === 'cost_categories') {
            this.fillResponseMessage(this.$gettext('Navigating to Cost Categories - comprehensive expenses report by cost category'))
            this.$router.push({ name: 'CostCategories' })
          } else if (response.data['resource_type'] === 'supplier_insight') {
            this.fillResponseMessage(this.$gettext('Navigating to Supplier Insight to analyze expenses made per supplier'))
            this.$router.push({ name: 'SupplierInsight' })
          } else if (response.data['resource_type'] === 'customer_insight') {
            this.fillResponseMessage(this.$gettext('Navigating to Customer Insight - comprehensive view of trips, hours and profit & loss for each customer side-by-side'))
            this.$router.push({ name: 'CustomerInsight' })
          } else if (response.data['resource_type'] === 'products_and_services') {
            this.fillResponseMessage(this.$gettext('Navigating to Products and Services - quantity and revenue overview of products and services you frequently invoice'))
            this.$router.push({ name: 'Products' })
          } else if (response.data['resource_type'] === 'financial_statements') {
            this.fillResponseMessage(this.$gettext('Navigating to Financial Statements - yearly financial Profit & Loss and Balance Sheet statements for tax and reporting purposes'))
            this.$router.push({ name: 'FinancialStatements' })
          } else if (response.data['resource_type'] === 'investments') {
            this.fillResponseMessage(this.$gettext('Navigating to Investments - comprehensive overview of all Balance Sheet costs and other assets with their write-off periods'))
            this.$router.push({ name: 'BalanceSheet' })
          } else if (response.data['resource_type'] === 'report_hours') {
            this.fillResponseMessage(this.$gettext('Navigating to Report Hours - complete report of all hours in your system'))
            this.$router.push({ name: 'HoursReport' })
          } else if (response.data['resource_type'] === 'revenue_expenses') {
            this.fillResponseMessage(this.$gettext('Navigating to Revenue and Expenses - detailed analysis of revenue, expenses and margins for your selected period'))
            this.$router.push({ name: 'RevenueExpenses' })
          } else if (response.data['resource_type'] === 'trip') {
            this.fillResponseMessage(this.$gettext('Navigating to Trips to register and track all your kilometers across business cars, private vehicles, public transport, or bicycles'))
            this.$router.push({ name: 'AddTrip' })
          } else if (response.data['resource_type'] === 'contact') {
            this.fillResponseMessage(this.$gettext('Navigating to Contacts to create new customers and suppliers for your business'))
            this.$router.push({ name: 'AddContact' })
          } else if (response.data['resource_type'] === 'project') {
            this.fillResponseMessage(this.$gettext('Navigating to Projects to create new projects related to your contacts'))
            // For now we navigate the user to the contacts page where they can access projects so we don't need to process specific contact ids
            this.$router.push({ name: 'Contacts' })
          } else if (response.data['resource_type'] === 'quotation') {
            this.fillResponseMessage(this.$gettext('Navigating to Quotations to create and share professional quotations with your customers. Track their status from draft to final invoice using our integrated invoicing system'))
            this.$router.push({ name: 'QuotationAdd' })
          } else if (response.data['resource_type'] === 'document') {
            this.fillResponseMessage(this.$gettext('Navigating to Documents to upload and manage important documents such as certificates, diplomas, terms and conditions, CVs, and more'))
            this.$router.push({ name: 'AddDocument' })
          } else if (response.data['resource_type'] === 'time_entry') {
            this.fillResponseMessage(this.$gettext('Navigating to Hours to start a timer or register your hours and activities manually. Get a real-time overview of your work in an intuitive calendar view'))
            this.$router.push({ name: 'Hours' })
          } else if (response.data['resource_type'] === 'bank_account') {
            this.fillResponseMessage(this.$gettext('Navigating to Banks to connect your bank account to your online accounting. Import all your business transactions and seamlessly link them to invoices and expenses'))
            this.$router.push({ name: 'Bank' })
          } else if (response.data['resource_type'] === 'temporary_file') {
            this.fillResponseMessage(this.$gettext('Navigating to Files to view and download files scheduled for download, including costs, invoices, bank statements, and more'))
            this.$router.push({ name: 'Files' })
          }

          // TODO When navigating to settings pages we should do a full redirect instead of a router push
          // maybe we can put a timeout? So we have time to show a message in the chat first
          else if (response.data['resource_type'] === 'invoice_settings') {
            this.fillResponseMessage(this.$gettext('Navigating to Invoice Settings to configure your professional invoice settings including VAT display, payment terms, custom messages, UBL attachments, hourly rates, and kilometer compensation'))
            // this.$router.push({ name: 'InvoiceSettings' })
            window.location.href = window.Urls['accounts_invoice_settings']()
          } else if (response.data['resource_type'] === 'brand') {
            this.fillResponseMessage(this.$gettext('Navigating to Brands to manage different trade names for your company brands. Easily select these trade names when creating professional invoices'))
            // this.$router.push({ name: 'Brands' })
            window.location.href = window.Urls['accounts_brands_settings']()
          } else if (response.data['resource_type'] === 'product') {
            this.fillResponseMessage(this.$gettext('Navigating to Products to create products with default prices and tax rates that streamline your invoice creation process'))
            // this.$router.push({ name: 'ProductList' })
            window.location.href = window.Urls['products']()
          } else if (response.data['resource_type'] === 'cost_type') {
            this.fillResponseMessage(this.$gettext('Navigating to Cost Types to create, manage, and archive cost categories for organizing your business expenses efficiently'))
            // this.$router.push({ name: 'CostTypesSettings' })
            window.location.href = window.Urls['accounts_costtypes_settings']()
          } else if (response.data['resource_type'] === 'car') {
            this.fillResponseMessage(this.$gettext('Navigating to Cars to register and manage your vehicle fleet, including license plates, brands, models, and business or private classification'))
            // this.$router.push({ name: 'Cars' })
            window.location.href = window.Urls['accounts_cars_settings']()
          } else if (response.data['resource_type'] === 'payment_link') {
            this.fillResponseMessage(this.$gettext('Navigating to Payment Links to enable easy customer payments via secure payment links. Automatically include payment links in your invoices for faster collection'))
            // this.$router.push({ name: 'IdealPayments' })
            window.location.href = window.Urls['accounts_ideal_payment_settings']()
          } else if (response.data['resource_type'] === 'webshop') {
            this.fillResponseMessage(this.$gettext('Navigating to Webshop to integrate your online sales directly into your Gekko account and automatically track VAT and other taxes. Real-time monitoring of all WooCommerce-powered webshop sales'))
            // this.$router.push({ name: 'Webshop' })
            window.location.href = window.Urls['accounts_webshop_settings']()
          } else if (response.data['resource_type'] === 'company_profile') {
            this.fillResponseMessage(this.$gettext('Navigating to Company Settings to edit your complete company profile including name, address, contact details, bank information, and tax identification numbers'))
            // this.$router.push({ name: 'CompanySettings' })
            window.location.href = window.Urls['accounts_manage_companies']()
          } else if (response.data['resource_type'] === 'user_profile') {
            this.fillResponseMessage(this.$gettext('Navigating to User Profile to manage your personal information, verify your email, update your password or delete your account'))
            // this.$router.push({ name: 'ProfileSettings' })
            window.location.href = window.Urls['accounts_edit_profile']()
          } else if (response.data['resource_type'] === 'subscription') {
            this.fillResponseMessage(this.$gettext('Navigating to Subscriptions to manage your subscription plans and billing information'))
            // this.$router.push({ name: 'Subscriptions' })
            window.location.href = window.Urls['subscriptions:manage']()
          } else if (response.data['resource_type'] === 'team_member') {
            this.fillResponseMessage(this.$gettext('Navigating to Team Member Settings to manage your team members and configure their access permissions'))
            // this.$router.push({ name: 'Team' })
            window.location.href = window.Urls['accounts_team_settings']()
          } else if (response.data['resource_type'] === 'dashboard') {
            this.fillResponseMessage(this.$gettext('Navigating to Dashboard to view your comprehensive business performance overview'))
            this.$router.push({ name: 'Dashboard' })
          }

          this.loading = false
          if (window.mixpanel) {
            window.mixpanel.track("Gekkobot: success");
          }
        }).catch((error) => {
          this.waitingForResponse = false
          let premsg = this.$gettext('Failed to process your message: ')
          let msg = this.$gettext('GekkoBot not responding.')
          if (error.status === 403) {
            premsg = this.$gettext('You\'ve reached your limit of free consultations: ')
            msg = this.$gettext('Please consider upgrading to Premium to keep using GekkoBot.')
            this.$store.commit('togglePremiumModal', 'web_ai_assistant')

            if (window.mixpanel) {
              window.mixpanel.track("Gekkobot: error - limit of free consultations");
            }
          } else {
            if (error.status === 405) {
              msg = this.$gettext('You are not allowed to use the chat bot feature.')
              if (window.mixpanel) {
                window.mixpanel.track("Gekkobot: error - not allowed");
              }
            } else if (error.status === 429) {
              msg = this.$gettext('You have already sent too many messages.')
              if (window.mixpanel) {
                window.mixpanel.track("Gekkobot: error - too many messages");
              }
            }
            this.$store.commit('setAlert', {
                title: this.$gettext('Error'),
                text: msg,
                variant: "danger",
                duration: 4
            })
          }
          this.fillResponseMessage(premsg + msg);
        })
      }, 200)
    },
    fillResponseMessage (response) {
      const response_item = this.conversation[this.conversation.length - 1]
      if (response_item) {
        if (response_item.author === 'ai' && !response_item.message) {
          response_item.message = this.parseGPTResponse(response);
          this.scrollConversation();
        }
      } else {
        response_item.message = this.parseGPTResponse(this.$gettext("Something went wrong, please try refreshing the page."));
        this.scrollConversation();
      }
    },
    parseGPTResponse (message) {
      let parsed_message = message

      // Replace new lines with <br> tags
      parsed_message = parsed_message.replace(/\n/g, '<br>')

      // Split the generated text into paragraphs
      const paragraphs = parsed_message.split('<br><br>');

      // Wrap each paragraph in <p> tags
      parsed_message = paragraphs.map(paragraph => `<p>${paragraph}</p>`).join('');

      return parsed_message
    },
    scrollConversation () {
    // conversation scroll bottom
      this.$nextTick(() => {
        const osInstance = this.$refs['ai-conversation'].osInstance();
        osInstance.scroll({
          y: '100%'
        }, 300);
      })
    },
    resetContext () {
      if (this.conversation.length < 2) {
        return
      }
      this.loading = true
      AiAssistantService.resetContext().then(() => {
        this.conversation = this.conversation.filter(obj => obj.message == this.intro);
        this.loading = false;
        this.waitingForResponse = false;
      }).catch(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext("Error"),
          text: this.$gettext("We could not reset the conversation."),
          variant: "danger",
          duration: 4
        });
      });
    }
  }
}
</script>

<style lang="scss">
.ai-assistant-wrapper {
  .message--text__content {
    p:last-child { margin-bottom: 0; }
  }
}
</style>
<style lang="scss" scoped>
.ai-assistant-wrapper {
  width: 100%; height: 100%;
  transition: all 0.3s ease;
  color: $colorTextBase;
  font-size: 1rem;
  .content-wrap { 
    height: 100%; width: 100%; 
    display: flex;
    flex-direction: column;
    transition: opacity 0.3s ease;
    .disclaimer {
      color: #a4a4a4;
      text-align: center;
      font-size: 12px;
      padding: 0.5rem;
      padding-bottom: 0;
    }
    .prompt-container {
      display: flex;
      flex-direction: column;
      padding: 0.5rem;
      background: $chatbotPanelBg;
    }
  }
}
</style>
<style lang="scss">

</style>